import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PPButton from '../../common/PPButton';
import PPModal from '../../common/PPModal';
import PPUploadComponent from '../../common/PPUploadComponent';
import Icon from '../../components/Icon';
import pageStyles from '../ProductionPanning/styles.module.scss';
import localStyles from './styles.module.scss';
import STODetailsModal from './STODetailsModal';
import UploadHistoryTable from './UploadHistoryTable';
import {
  downloadRetailIstOutwardSample,
  fetchRetailIstOutwardStos,
  fetchRetailIstOutwardUploadHistory,
  uploadRetailIstOutwardFile,
} from './utils';

const HISTORY_PAGE_SIZE = 10;
const UPLOAD_SUCCESS_HEADING = 'Submitted successfully';
const DOWNLOAD_SUCCESS_HEADING = 'Download completed';
const ERROR_MODAL_HEADING = 'Action Failed';

function RetailISTOutwardUploadPage() {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(HISTORY_PAGE_SIZE);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [historyRefreshCounter, setHistoryRefreshCounter] = useState(0);
  const [shouldPollHistory, setShouldPollHistory] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActionSuccess, setIsActionSuccess] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalHeading, setModalHeading] = useState(ERROR_MODAL_HEADING);
  const [stoModalOpen, setStoModalOpen] = useState(false);
  const [stoModalUploadId, setStoModalUploadId] = useState('');
  const [stoModalUploadedBy, setStoModalUploadedBy] = useState('');
  const [stoModalData, setStoModalData] = useState([]);
  const [stoModalLoading, setStoModalLoading] = useState(false);
  const [stoModalError, setStoModalError] = useState('');
  const { authUserResponse } = useSelector((state) => state.authReducer);
  const user = authUserResponse?.email_id;
  const isSilentPollingRef = useRef(false);

  const shouldPollForRows = (rows = []) =>
    rows.some((row) => {
      const normalizedStatus = String(row?.status || '')
        .trim()
        .toUpperCase();
      return normalizedStatus && normalizedStatus !== 'COMPLETED' && normalizedStatus !== 'FAILED';
    });

  const showFeedbackModal = (success, heading, message = '') => {
    setIsActionSuccess(success);
    setModalHeading(heading);
    setModalMessage(message);
    setIsModalOpen(true);

    if (success) {
      setTimeout(() => {
        setIsModalOpen(false);
      }, 2500);
    }
  };

  const handleFileUpload = (files) => {
    const file = files?.[0] || null;

    if (file && !file.name.toLowerCase().endsWith('.csv')) {
      setSelectedFile(null);
      setSubmitButtonDisabled(true);
      showFeedbackModal(false, ERROR_MODAL_HEADING, 'Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    setSubmitButtonDisabled(!file);
  };

  useEffect(() => {
    let isMounted = true;

    const loadUploadHistory = async () => {
      if (!user) return;

      try {
        if (!isSilentPollingRef.current) {
          setHistoryLoading(true);
        }
        setHistoryError('');
        const response = await fetchRetailIstOutwardUploadHistory(
          user,
          currentPage,
          pageSize,
        );

        if (!isMounted) return;

        setHistoryRows(response?.data || []);
        setTotalPages(response?.meta?.totalPages || 0);
        setTotalElements(response?.meta?.totalElements || 0);
        setShouldPollHistory(shouldPollForRows(response?.data || []));
      } catch (error) {
        if (!isMounted) return;
        setHistoryRows([]);
        setTotalPages(0);
        setTotalElements(0);
        setHistoryError(error?.message || 'Failed to fetch upload history');
        setShouldPollHistory(false);
      } finally {
        if (isMounted) {
          setHistoryLoading(false);
        }
        isSilentPollingRef.current = false;
      }
    };

    loadUploadHistory();

    return () => {
      isMounted = false;
    };
  }, [currentPage, pageSize, user, historyRefreshCounter]);

  useEffect(() => {
    if (!user || !shouldPollHistory) return undefined;

    const pollingInterval = setInterval(() => {
      isSilentPollingRef.current = true;
      setHistoryRefreshCounter((prev) => prev + 1);
    }, 5000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [user, shouldPollHistory]);

  const handleSampleDownload = async () => {
    try {
      setIsDownloading(true);
      await downloadRetailIstOutwardSample(user);
      showFeedbackModal(
        true,
        DOWNLOAD_SUCCESS_HEADING,
        'The sample CSV has been saved to your device.',
      );
    } catch (error) {
      showFeedbackModal(
        false,
        ERROR_MODAL_HEADING,
        error?.message || 'Failed to download sample CSV',
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || isUploading) return;

    try {
      setIsUploading(true);
      setSubmitButtonDisabled(true);
      const response = await uploadRetailIstOutwardFile(user, selectedFile);

      showFeedbackModal(
        true,
        UPLOAD_SUCCESS_HEADING,
        response?.message || 'IST outward file uploaded successfully',
      );

      setSelectedFile(null);
      setHistoryError('');
      setCurrentPage(0);
      setHistoryRefreshCounter((prev) => prev + 1);
    } catch (error) {
      showFeedbackModal(
        false,
        ERROR_MODAL_HEADING,
        error?.message || 'Failed to upload IST outward file',
      );
      setSubmitButtonDisabled(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadIdClick = async (stosCreated, uploadId, uploadedBy) => {
    setStoModalUploadId(uploadId);
    setStoModalUploadedBy(uploadedBy || '');
    setStoModalData([]);
    setStoModalError('');
    setStoModalLoading(true);
    setStoModalOpen(true);

    try {
      const response = await fetchRetailIstOutwardStos(user, stosCreated);
      setStoModalData(response?.data || []);
    } catch (error) {
      setStoModalError(error?.message || 'Failed to fetch STO details');
    } finally {
      setStoModalLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(page, 0));
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(0);
  };

  return (
    <div className={localStyles.page_content}>
      <div className={localStyles.page_header}>
        <h2 className={`${pageStyles.page_heading} ${localStyles.page_title}`}>
          Retail IST Outward Upload
        </h2>
        <div className={localStyles.sample_download_btn}>
          <PPButton
            variant="outlined"
            className={localStyles.sample_download_button}
            startIcon={<Icon name="download_icon_blue" width="18" height="18" />}
            onClick={handleSampleDownload}
            disabled={isDownloading}
            isAnimating={isDownloading}>
            Download Sample CSV
          </PPButton>
        </div>
      </div>
      <div className={localStyles.page_scroll_content}>
        <div className={localStyles.upload_card}>
          <div className={localStyles.upload_section}>
            <PPUploadComponent
              data-testid="retail_ist_outward_upload"
              title="IST Plan (CSV File)"
              uploadBoxTitle={selectedFile?.name || 'Upload or Drop CSV file here'}
              firstBtnLabel="Browse File"
              firstIcon={<Icon name="upload_file" width="20" height="20" />}
              handleFileUpload={handleFileUpload}
              selectedFile={selectedFile}
              uploadFile={true}
              fileButtonDisabled={false}
              turnBluecolor={Boolean(selectedFile)}
              drag
              dragProps={{
                count: 1,
                formats: ['.csv'],
                onUpload: handleFileUpload,
              }}
              acceptFormat=".csv"
              fullWidthUploadAction
            />
            <div className={localStyles.submit_div}>
              <PPButton
                variant="contained"
                classes={{
                  root: submitButtonDisabled
                    ? localStyles.disabled_button
                    : localStyles.enabled_button,
                }}
                onClick={handleSubmit}
                isAnimating={isUploading}
                disabled={submitButtonDisabled}>
                Submit
              </PPButton>
            </div>
          </div>
        </div>
        <UploadHistoryTable
          rows={historyRows}
          loading={historyLoading}
          error={historyError}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          totalElements={totalElements}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          onUploadIdClick={handleUploadIdClick}
        />
      </div>
      <PPModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        icon={
          isActionSuccess ? (
            <Icon name="green_tick" height={80} width={80} />
          ) : (
            <Icon name="warning" height={76} width={78} />
          )
        }
        modalHeading={modalHeading}
        subMsg={modalMessage}
        success={isActionSuccess}
      />
      <STODetailsModal
        open={stoModalOpen}
        onClose={() => setStoModalOpen(false)}
        uploadId={stoModalUploadId}
        uploadedBy={stoModalUploadedBy}
        data={stoModalData}
        loading={stoModalLoading}
        error={stoModalError}
      />
    </div>
  );
}

export default RetailISTOutwardUploadPage;
